import React, { useEffect } from 'react';
import { Delete } from '../ProjectIcons';
import CustomObjectTemplate from './CustomObjectTemplate';
import CustomCheckbox from './CustomCheckbox';
import { useTranslation } from 'react-i18next';

type ArrayElementType = string | { key: string; value: any };
interface CustomArrayFieldProps {
  schema: any;
  formData: any;
  onChange: (value: any) => void;
}

export const CustomArrayField: React.FC<CustomArrayFieldProps> = ({ schema, formData, onChange }) => {
  const { title, items } = schema;
  const itemsData = formData || [];
  const { t } = useTranslation();

  // Files vars
  const accept = 'text/xml'; // Default to XML files (for now)

  useEffect(() => {
    // Ensure that formData is initialized properly
    if (!formData) {
      onChange([]);
    }
  }, [formData, onChange]);

  const handleAddItem = () => {
    let newItem: any;
    if (typeof items === 'object' && items !== null && 'enum' in items && items.enum) {
      newItem = items.enum[0]; // Default to the first enum value
    } else {
      newItem = ''; // Default to an empty string for arrays of strings
    }
    const updatedItems = [...itemsData, newItem];
    onChange(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...itemsData];
    updatedItems.splice(index, 1);
    onChange(updatedItems);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const files = Array.from(event.target.files || []);
    const xmlFiles = files.filter(file => file.type === 'text/xml');

    const updatedItems = [...itemsData];
    updatedItems[index] = xmlFiles.length > 0 ? xmlFiles[0] : null;
    onChange(updatedItems);
  };

  return (
    <div className="my-2 w-full">
      <div className="flex flex-row items-start justify-between w-full gap-6">
        <div className="w-1/2 flex flex-col pt-2">
          <label className="font-semibold dark:text-white">{title}</label>
          {schema && schema.description && (
            <div className="mt-1">
              <span className="text-secondary-gray dark:text-neutral-gray-300 text-sm">
                {schema.description}
              </span>
            </div>
          )}
        </div>
        <div className="w-1/2">
          <ul className="w-full">
            {schema.uniqueItems ? (
              items.enum.map((enumItem: any, enumIndex: number) => (
                <li key={enumIndex} className="flex flex-col">
                  <CustomCheckbox
                    value={enumIndex}
                    label={enumItem}
                    checked={itemsData.includes(enumItem)}
                    itemsData={itemsData}
                    enumItem={enumItem}
                    onChange={() => {
                      const updatedItems = !itemsData.includes(enumItem)
                        ? [...itemsData, enumItem]
                        : itemsData.filter((item: any) => item !== enumItem);
                      onChange(updatedItems);
                    }}
                    formData={formData}
                  />
                </li>
              ))
            ) : (
              itemsData.map((item: ArrayElementType, index: number) => (
                <li key={index} className="flex flex-row mb-3 border border-gray-lines dark:border-neutral-gray-600 w-full p-2 dark:bg-neutral-gray-700 transition-all duration-300">
                  {typeof items === 'object' && items !== null && 'enum' in items ? (
                    <div className="flex-1">
                      <select
                        className="border border-gray-lines dark:border-neutral-gray-600 rounded py-1 px-2 text-off-black dark:text-white dark:bg-neutral-gray-700 w-full transition-all duration-300"
                        value={typeof item === 'string' ? item : item.key}
                        onChange={(e) => {
                          const value = e.target.value;
                          const updatedItems = [...itemsData];
                          updatedItems[index] = typeof item === 'string' ? value : { key: value, value: value };
                          onChange(updatedItems);
                        }}
                      >
                        {typeof items === 'object' && items !== null && 'enum' in items && items.enum ? (
                          items.enum.map((enumItem: any, enumIndex: number) => (
                            <option key={enumIndex} value={enumItem}>
                              {enumItem}
                            </option>
                          ))
                        ) : null}
                      </select>
                    </div>
                  ) : typeof items === 'object' && items !== null && items.type === 'object' ? (
                    <div className="flex-1">
                      <CustomObjectTemplate
                        schema={items}
                        formData={typeof item === 'object' && item !== null ? item : {}}
                        onChange={(value: any) => {
                          const updatedItems = [...itemsData];
                          updatedItems[index] = value;
                          onChange(updatedItems);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex-1">
                      <input
                        type="text"
                        className="border border-gray-lines dark:border-neutral-gray-600 rounded py-1 px-2 text-off-black dark:text-white dark:bg-neutral-gray-700 w-full transition-all duration-300"
                        value={(item as string) || ''}
                        placeholder={t('Enter your text here') || ''}
                        onChange={(e) => {
                          const updatedItems = [...itemsData];
                          updatedItems[index] = e.target.value;
                          onChange(updatedItems);
                        }}
                      />
                    </div>
                  )}
                  <button type="button" className="bg-very-light-orange dark:bg-orange-700 text-orange dark:text-orange-300 py-2 px-2 mx-2 h-12 transition-all duration-300" onClick={() => handleRemoveItem(index)}>
                    <Delete />
                  </button>
                </li>
              ))
            )}
          </ul>
          {!schema.uniqueItems && (
            <div className="flex justify-end mt-2">
              <button
                type="button"
                className="bg-light-green dark:bg-dark-green-900 rounded-sm text-dark-green dark:text-dark-green-100 font-semibold py-2 px-3 transition-all duration-300"
                onClick={handleAddItem}
              >
                Add Item
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
